import { path, createCachedSelector, defaultKeySelector } from '@cbridge-utils/redux';
import { createDefaultCachedSelectorOptions } from '@cbridge-utils/reselect';
import { compareStrings } from '@cbridge-utils/object';
import { NetworkStatus } from '@cbridge-utils/network';
import { INavigationBlockItemBase } from '@cbridge-tools/navigation-block';
import { coverageReportListSelector } from '@cbridge-packages/coverage-report';
import { coverageServerListSelector } from '@cbridge-packages/coverage-server';
import { generateCoverageReportRoute } from '@cbridge-packages/routing-client-admin';

export interface ICoverageReportNavGroup {
  serverId: number;
  serverName: string;
  reports: INavigationBlockItemBase[];
}

// Reports navigation is two-level: grouped by owning server (a report belongs to one server).
// Only servers that have at least one report appear.
export const externalReportingNavigationPropsSelector = createCachedSelector(
  [
    path(coverageReportListSelector).list(),
    path(coverageReportListSelector).networkStatus(),
    path(coverageServerListSelector).list(),
  ],
  (reports, networkStatus, servers) => {
    const serverNameById = new Map(servers.map((server) => [server.serverId, server.name]));

    const groupsByServer = new Map<number, ICoverageReportNavGroup>();
    reports.forEach((report) => {
      let group = groupsByServer.get(report.serverId);
      if (!group) {
        group = {
          serverId: report.serverId,
          serverName: serverNameById.get(report.serverId) ?? String(report.serverId),
          reports: [],
        };
        groupsByServer.set(report.serverId, group);
      }
      group.reports.push({
        value: report.reportId,
        label: report.name,
        href: generateCoverageReportRoute(report.reportId),
      });
    });

    const groups = Array.from(groupsByServer.values())
      .sort((a, b) => compareStrings(a.serverName, b.serverName));

    return {
      groups,
      isLoading: networkStatus !== NetworkStatus.Done && networkStatus !== NetworkStatus.Failed,
    };
  },
)({
  ...createDefaultCachedSelectorOptions(),
  keySelector: defaultKeySelector,
});
